import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProfessionClassesApiService } from '@digilize/shared/data/services';
import {
  DigitalVacancyModel,
  Distance,
  HoursPerWeekPreference,
  Option,
  PostingDate,
  ProfessionClassModel,
  SearchMode,
  UserPreferredEducationLevel,
  VacancySortOrder,
} from '@digilize/shared/definitions/src';
import { getErrorMessage } from '@digilize/shared/feature/modules/auth';
import { createAndObserveIntersection } from '@digilize/shared/utils/helpers/src';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs';

import { CvService } from '../../../../../services/cv.service';
import { VacaniesService } from '../../../../../services/vacanies.service';
import { Vacancy } from '../../models/vacancy.model';
import { VacancyDetailData } from '../vacancy-detail/vacancy-detail.component';

const PAGE_SIZE = 20;

const DISTANCE_STEPS: { value: Distance; labelKey: string }[] = [
  { value: Distance.Upto10km, labelKey: 'work_preferences.distance_10' },
  { value: Distance.Upto25km, labelKey: 'work_preferences.distance_25' },
  { value: Distance.Upto50km, labelKey: 'work_preferences.distance_50' },
  { value: Distance.Any, labelKey: 'work_preferences.distance_50_plus' },
];

const EDUCATION_LEVEL_OPTIONS: { value: UserPreferredEducationLevel; labelKey: string }[] = [
  { value: UserPreferredEducationLevel.Lbo, labelKey: 'shared.filters.lbo' },
  { value: UserPreferredEducationLevel.Vmbo, labelKey: 'shared.filters.vmbo' },
  { value: UserPreferredEducationLevel.Havo, labelKey: 'shared.filters.havo' },
  { value: UserPreferredEducationLevel.Vwo, labelKey: 'shared.filters.vwo' },
  { value: UserPreferredEducationLevel.Mbo, labelKey: 'shared.filters.mbo' },
  { value: UserPreferredEducationLevel.Hbo, labelKey: 'shared.filters.hbo' },
  { value: UserPreferredEducationLevel.Wo, labelKey: 'shared.filters.wo' },
  { value: UserPreferredEducationLevel.Phd, labelKey: 'shared.filters.phd' },
];

const HOURS_OPTIONS: { value: HoursPerWeekPreference; labelKey: string }[] = [
  { value: HoursPerWeekPreference.H32_40, labelKey: 'work_preferences.fulltime' },
  { value: HoursPerWeekPreference.H24_32, labelKey: 'work_preferences.parttime_24_32' },
  { value: HoursPerWeekPreference.H0_24, labelKey: 'work_preferences.parttime_0_24' },
  { value: HoursPerWeekPreference.HFlexible, labelKey: 'work_preferences.flexible' },
];

const POSTED_OPTIONS: { value: PostingDate; labelKey: string }[] = [
  { value: PostingDate.Today, labelKey: 'vacancies.list.filter_today' },
  { value: PostingDate.OneWeek, labelKey: 'vacancies.list.filter_last_week' },
  { value: PostingDate.OneMonth, labelKey: 'vacancies.list.filter_last_month' },
];

const SORT_OPTIONS: { key: string; value: VacancySortOrder; labelKey: string; order_column_descending: boolean }[] = [
  {
    key: 'match_desc',
    value: VacancySortOrder.Match,
    labelKey: 'vacancies.list.sort_match_desc',
    order_column_descending: true,
  },
  {
    key: 'match_asc',
    value: VacancySortOrder.Match,
    labelKey: 'vacancies.list.sort_match_asc',
    order_column_descending: false,
  },
  // { key: 'date_desc', value: VacancySortOrder.Date, labelKey: 'vacancies.list.sort_date_desc', order_column_descending: true },
  // { key: 'salary_desc', value: VacancySortOrder.Salary, labelKey: 'vacancies.list.sort_salary_desc', order_column_descending: true },
];

@Component({
  selector: 'bwc-vacancies-list',
  templateUrl: './vacancies-list.component.html',
  styleUrls: ['./vacancies-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VacanciesListComponent implements OnInit, OnDestroy {
  private endOfListSub?: Subscription;

  @ViewChild('endOfList') set endOfListRef(ref: ElementRef | undefined) {
    this.endOfListSub?.unsubscribe();
    if (ref) {
      this.endOfListSub = createAndObserveIntersection(ref)
        .pipe(takeUntil(this.destroy$))
        .subscribe((isVisible) => {
          if (isVisible && this.hasMore$.getValue() && !this.loading) {
            this.loadVacancies(true);
          }
        });
    }
  }

  private readonly cvService = inject(CvService);
  private readonly vacaniesService = inject(VacaniesService);
  private readonly professionClassesApi = inject(ProfessionClassesApiService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  detailData: VacancyDetailData | null = null;

  private professionClasses: ProfessionClassModel[] = [];

  vacancies$ = new BehaviorSubject<Vacancy[]>([]);
  hasMore$ = new BehaviorSubject<boolean>(false);
  errorMessage: string | null = null;
  locationControl = new FormControl<string>('', { nonNullable: true });
  distanceSliderValue = 0;
  distanceFilter = Distance.Upto10km;
  distanceLabel = '';
  distanceSteps = DISTANCE_STEPS;
  matchBasis: SearchMode = SearchMode.ByWorkExperience;
  showMatchTooltip = false;
  loading = false;
  totalRecords = 0;
  searchMode = SearchMode;
  // Filter / sort state
  filterPanelOpen = false;
  fieldOfWork: string | null = null;
  educationLevel: UserPreferredEducationLevel | null = null;
  hoursRange: HoursPerWeekPreference | null = null;
  postingDate: PostingDate | null = null;
  sortKey = 'match_desc';

  // Bound to lib-single-select-search inputs (each holds the visible label string)
  fieldOfWorkControl = new FormControl('');
  educationControl = new FormControl('');
  hoursControl = new FormControl('');
  postedControl = new FormControl('');
  sortControl = new FormControl('');

  fieldOfWorkOptions: Option[] = [];
  educationOptions: Option[] = [];
  hoursOptions: Option[] = [];
  postedOptions: Option[] = [];
  sortOptionsList: Option[] = [];

  constructor() {}

  ngOnInit(): void {
    this.translateService
      .get('vacancies.list.sort_button')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initFilterOptions();
        this.loadPreferencesAndVacancies();
      });
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initFilterOptions();
      this.syncFieldOfWorkControlLabel();
      this.syncEducationControlLabel();
      this.syncHoursControlLabel();
    });
    this.professionClassesApi
      .getProfessionClasses()
      .pipe(takeUntil(this.destroy$))
      .subscribe((classes) => {
        this.professionClasses = classes;
        this.initFilterOptions();
        this.syncFieldOfWorkControlLabel();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get vacancies(): Vacancy[] {
    return this.vacancies$.getValue();
  }

  private updateHasMore(): void {
    this.hasMore$.next(this.vacancies.length < this.totalRecords);
  }

  onMatchBasisChange(basis: SearchMode): void {
    this.matchBasis = basis;
    this.vacancies$.next([]);
    this.loadVacancies();
  }

  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
  }

  onFieldOfWorkSelected(option: Option): void {
    const next = option.val === '' ? null : option.val;
    if (this.fieldOfWork === next) return;
    this.fieldOfWork = next;
    this.reloadAfterFilterChange();
  }

  onEducationSelected(option: Option): void {
    const next = option.val === '' ? null : (Number(option.val) as UserPreferredEducationLevel);
    if (this.educationLevel === next) return;
    this.educationLevel = next;
    this.reloadAfterFilterChange();
  }

  onHoursSelected(option: Option): void {
    const next = option.val === '' ? null : (Number(option.val) as HoursPerWeekPreference);
    if (this.hoursRange === next) return;
    this.hoursRange = next;
    this.reloadAfterFilterChange();
  }

  onPostedSelected(option: Option): void {
    const next = option.val === '' ? null : (Number(option.val) as PostingDate);
    if (this.postingDate === next) return;
    this.postingDate = next;
    this.reloadAfterFilterChange();
  }

  onSortSelected(option: Option): void {
    if (!option.val || this.sortKey === option.val) return;
    this.sortKey = option.val;
    this.reloadAfterFilterChange();
  }

  get activeFilterCount(): number {
    let n = 0;
    if (this.fieldOfWork != null) n++;
    if (this.educationLevel != null) n++;
    if (this.hoursRange != null) n++;
    if (this.postingDate != null) n++;
    return n;
  }

  private reloadAfterFilterChange(): void {
    this.vacancies$.next([]);
    this.loadVacancies();
  }

  onDistanceSliderChange(sliderValue: number): void {
    this.distanceSliderValue = sliderValue;
    const step = DISTANCE_STEPS[sliderValue];
    if (step) {
      this.distanceFilter = step.value;
      this.updateDistanceLabel();
    }
  }

  isLiked(vacancy: Vacancy): boolean {
    return vacancy.is_favorite;
  }

  toggleLike(vacancy: Vacancy): void {
    this.vacaniesService.toggleFavoriteVacancy(vacancy.id).subscribe();
    vacancy.is_favorite = !vacancy.is_favorite;
    this.vacancies$.next([...this.vacancies]);
  }

  openDetail(vacancy: Vacancy): void {
    const index = this.vacancies.findIndex((v) => v.id === vacancy.id);
    if (index < 0) return;
    this.detailData = {
      vacancies$: this.vacancies$.asObservable(),
      initialIndex: index,
      searchMode: this.matchBasis,
      hasMore$: this.hasMore$.asObservable(),
      loadMore: () => this.loadVacancies(true),
      onFavoriteToggled: (id, isSaved) => this.syncFavorite(id, isSaved),
    };
  }

  closeDetail(): void {
    this.detailData = null;
  }

  private syncFavorite(id: string | number, isSaved: boolean): void {
    const list = this.vacancies;
    const idStr = String(id);
    const match = list.find((v) => String(v.id) === idStr);
    if (!match || match.is_favorite === isSaved) return;
    match.is_favorite = isSaved;
    this.vacancies$.next([...list]);
  }

  private initFilterOptions(): void {
    const t = (key: string) => this.translateService.instant(key);
    this.fieldOfWorkOptions = [
      { text: t('vacancies.list.filter_all_fields'), val: '' },
      ...this.professionClasses.map((pc) => ({ text: pc.name, val: pc.id })),
    ];
    this.educationOptions = [
      { text: t('vacancies.list.filter_all_levels'), val: '' },
      ...EDUCATION_LEVEL_OPTIONS.map((e) => ({ text: t(e.labelKey), val: String(e.value) })),
    ];
    this.hoursOptions = [
      { text: t('vacancies.list.filter_all_hours'), val: '' },
      ...HOURS_OPTIONS.map((h) => ({ text: t(h.labelKey), val: String(h.value) })),
    ];
    this.postedOptions = [
      { text: t('vacancies.list.filter_all_dates'), val: '' },
      ...POSTED_OPTIONS.map((p) => ({ text: t(p.labelKey), val: String(p.value) })),
    ];
    this.sortOptionsList = SORT_OPTIONS.map((s) => ({ text: t(s.labelKey), val: s.key }));
    const currentSort = this.sortOptionsList.find((o) => o.val === this.sortKey);
    if (currentSort) {
      this.sortControl.setValue(currentSort.text);
    }
  }

  private syncFieldOfWorkControlLabel(): void {
    const val = this.fieldOfWork ?? '';
    const opt = this.fieldOfWorkOptions.find((o) => o.val === val);
    this.fieldOfWorkControl.setValue(opt?.text ?? '');
  }

  private syncEducationControlLabel(): void {
    const val = this.educationLevel == null ? '' : String(this.educationLevel);
    const opt = this.educationOptions.find((o) => o.val === val);
    this.educationControl.setValue(opt?.text ?? '');
  }

  private syncHoursControlLabel(): void {
    const val = this.hoursRange == null ? '' : String(this.hoursRange);
    const opt = this.hoursOptions.find((o) => o.val === val);
    this.hoursControl.setValue(opt?.text ?? '');
  }

  private loadPreferencesAndVacancies(): void {
    this.cvService
      .getCV()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cv) => {
          const prefs = cv.vacancy_preferences;
          if (prefs) {
            if (prefs.city) {
              this.locationControl.setValue(prefs.city);
            }
            if (prefs.distance && (prefs.distance as number) !== Distance.Unknown) {
              this.distanceFilter = prefs.distance;
              const sliderIndex = DISTANCE_STEPS.findIndex((s) => s.value === prefs.distance);
              this.distanceSliderValue = sliderIndex >= 0 ? sliderIndex : 0;
            }
            if (prefs.education_level != null) {
              this.educationLevel = prefs.education_level;
              this.syncEducationControlLabel();
            }
            if (prefs.hours_range != null) {
              this.hoursRange = prefs.hours_range;
              this.syncHoursControlLabel();
            }
          }
          this.updateDistanceLabel();
          this.loadVacancies();
        },
        error: () => {
          this.updateDistanceLabel();
          this.loadVacancies();
        },
      });
  }

  private updateDistanceLabel(): void {
    const step = DISTANCE_STEPS[this.distanceSliderValue];
    if (!step) return;
    this.distanceLabel = this.translateService.instant(step.labelKey);
  }

  private loadVacancies(append = false): void {
    this.loading = true;
    const start = append ? this.vacancies.length : 0;

    const params: Record<string, unknown> = {
      search_mode: this.matchBasis,
      location: this.locationControl.value ?? '',
      start,
      length: PAGE_SIZE,
    };
    if (this.distanceFilter !== Distance.Any) {
      params['distance'] = this.distanceFilter;
    } else {
      delete params['location'];
    }
    if (this.educationLevel != null) {
      params['education_level'] = [this.educationLevel];
    }
    if (this.hoursRange != null) {
      params['working_hours'] = this.hoursRange;
    }
    if (this.postingDate != null) {
      params['posting_date'] = this.postingDate;
    }
    if (this.fieldOfWork != null) {
      params['profession_class'] = this.fieldOfWork;
    }
    const sort = SORT_OPTIONS.find((s) => s.key === this.sortKey) ?? SORT_OPTIONS[0];
    params['sort_order'] = sort.value;
    params['order_column_descending'] = sort.order_column_descending;

    this.vacaniesService.getMyVacancies(params).subscribe({
      next: (res) => {
        const now = new Date();
        const mapped = (res.data || []).map((dto) => this.mapToVacancy(dto, now));
        this.vacancies$.next(append ? [...this.vacancies, ...mapped] : mapped);
        this.totalRecords = res.recordsFiltered ?? res.recordsTotal ?? 0;
        this.updateHasMore();
        this.errorMessage = null;
        this.loading = false;
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.status === 400) {
          const apiError = httpError.error?.error;
          this.errorMessage = getErrorMessage(apiError, (key) => this.translateService.instant(key)) || null;
        }
        this.loading = false;
      },
    });
  }

  private mapToVacancy(dto: DigitalVacancyModel, now: Date): Vacancy {
    const activated = dto.activated ? new Date(dto.activated) : null;
    const diffDays = activated ? Math.floor((now.getTime() - activated.getTime()) / (1000 * 60 * 60 * 24)) : Infinity;

    return {
      id: dto.id as unknown as number,
      title: dto.job_title || '',
      company: dto.organization || '',
      location: dto.location || '',
      tags: dto.tags || [],
      skills: [],
      skillCategories: { knowledge: [], softSkills: [], languages: [] },
      matchScore: dto.match_score ?? 0,
      isNew: diffDays <= 7,
      is_favorite: dto.is_favorite,
    };
  }
}
